import { Box, Paper, Typography, useTheme } from '@mui/material';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { MetricRendererProps } from './rendererTypes';
import { getMetricDisplayName } from './rendererTypes';
import TileMetaActions from '../TileMetaActions';
import { tileMetaTitlePad } from '../../utils/tileMeta';

type TickProps = {
  x?: number;
  y?: number;
  index?: number;
  payload?: { value?: string };
};

export default function VerticalBarChartRenderer({ config, data }: MetricRendererProps) {
  const theme = useTheme();
  const title = getMetricDisplayName(data, config);
  const items = data.breakdown ?? [];
  const chartData = items.map(item => ({
    name: item.label,
    value: item.value,
  }));

  const renderTick = ({ x = 0, y = 0, index = 0 }: TickProps) => {
    const item = items[index];
    if (!item) {
      return <g transform={`translate(${x},${y})`} />;
    }

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={14}
          textAnchor="middle"
          fill={theme.palette.text.secondary}
          fontSize={11}
        >
          {item.label}
        </text>
        <text
          x={0}
          y={0}
          dy={32}
          textAnchor="middle"
          fill={theme.palette.text.primary}
          fontSize={13}
          fontWeight={700}
        >
          {item.value}
        </text>
        <text
          x={0}
          y={0}
          dy={48}
          textAnchor="middle"
          fill={theme.palette.success.main}
          fontSize={11}
          fontWeight={600}
        >
          +{item.value}
        </text>
      </g>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        width: '100%',
        position: 'relative',
      }}
    >
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      {title ? (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1.5,
            lineHeight: 1.3,
            pr: tileMetaTitlePad(data.meta),
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Box sx={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
            <XAxis
              dataKey="name"
              interval={0}
              tick={renderTick}
              height={64}
              axisLine={false}
              tickLine={false}
            />
            <YAxis tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Bar dataKey="value" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
